import { useUser, useReverification } from "@clerk/react";
// import { isClerkRuntimeError, isReverificationCancelledError } from '@clerk/clerk-react/errors';
import { AppLoading } from "@/components/app-loading";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, CheckCircle, XCircle, Trash2 } from "lucide-react";

interface MailPermissionGuardProps {
  children: React.ReactNode;
}

export const MailPermissionGuard = ({ children }: MailPermissionGuardProps) => {
  const { user, isLoaded: isUserLoaded } = useUser();

  const connectAccount = useReverification(
    async (provider: "google" | "microsoft") => {
      if (!user) throw new Error("not_logged_in");

      const strategy =
        provider === "google" ? "oauth_google" : "oauth_microsoft";
      const existingAccount = user.externalAccounts?.find(
        (account) => account.provider === provider,
      );

      const requiredScopes =
        provider === "google"
          ? [
              "https://www.googleapis.com/auth/gmail.send",
              "https://www.googleapis.com/auth/gmail.readonly",
            ]
          : [
              "https://graph.microsoft.com/User.Read",
              "https://graph.microsoft.com/Mail.Read",
              "https://graph.microsoft.com/Mail.Send",
              "https://graph.microsoft.com/Mail.ReadWrite",
            ];

      if (existingAccount) {
        return existingAccount.reauthorize({
          redirectUrl: `${window.location.origin}${location.pathname}`,
          additionalScopes: requiredScopes,
          oidcPrompt: "consent",
        });
      }
      return user.createExternalAccount({
        strategy,
        redirectUrl: `${window.location.origin}${location.pathname}`,
        additionalScopes: requiredScopes,
        oidcPrompt: "consent",
      });
    },
  );

  const handleConnect = async (provider: "google" | "microsoft") => {
    if (!user) {
      toast.error(
        `You must be logged in to connect a ${provider === "google" ? "Google" : "Microsoft"} account.`,
      );
      return;
    }

    try {
      const response = await connectAccount(provider);
      if (response?.verification?.externalVerificationRedirectURL) {
        window.location.href =
          response.verification.externalVerificationRedirectURL.href;
      }
    } catch (error: any) {
      console.error(`Error connecting ${provider} account:`, error);
      const errorMessage =
        error?.errors?.[0]?.long_message || error?.errors?.[0]?.message;
      toast.error(
        errorMessage ||
          `Failed to connect ${provider === "google" ? "Google" : "Microsoft"} account. Please try again.`,
      );
    }
  };

  const disconnectAccount = useReverification(
    async (provider: "google" | "microsoft") => {
      const account = user?.externalAccounts?.find(
        (acc) => acc.provider === provider,
      );
      if (!account) return;
      await account.destroy();
    },
  );

  const handleDisconnect = async (provider: "google" | "microsoft") => {
    try {
      await disconnectAccount(provider);
      toast.success(
        `${provider === "google" ? "Google" : "Microsoft"} account disconnected`,
      );
    } catch (error) {
      // if (isClerkRuntimeError(error) && isReverificationCancelledError(error)) {
      //   toast.error("Reverification cancelled");
      // } else {
      //   toast.error("Failed to disconnect account");
      // }
    }
  };

  if (!isUserLoaded) {
    return <AppLoading />;
  }

  const googleAccount = user?.externalAccounts?.find(
    (account) => account.provider === "google",
  );
  const microsoftAccount = user?.externalAccounts?.find(
    (account) => account.provider === "microsoft",
  );

  // Check Google scopes
  const googleScopes: string = googleAccount?.approvedScopes || "";
  const hasGmailSend = googleScopes.includes("gmail.send");
  const hasGmailRead = googleScopes.includes("gmail.readonly");
  const googlePermissionsGranted = hasGmailSend && hasGmailRead;

  // Check Microsoft scopes
  const microsoftScopes: string = microsoftAccount?.approvedScopes || "";
  const hasMicrosoftMailRead = microsoftScopes.includes("Mail.Read");
  const hasMicrosoftMailSend = microsoftScopes.includes("Mail.Send");
  const hasMicrosoftMailReadWrite = microsoftScopes.includes("Mail.ReadWrite");
  const microsoftPermissionsGranted =
    hasMicrosoftMailRead && hasMicrosoftMailSend && hasMicrosoftMailReadWrite;

  // Allow access if either provider has all required permissions
  const requireOauth = user?.publicMetadata?.require_oauth === true;
  const allPermissionsGranted =
    googlePermissionsGranted || microsoftPermissionsGranted;

  if (!allPermissionsGranted && requireOauth) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-linear-to-br from-muted/50 to-muted p-4">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="flex bg-primary h-16 w-16 rounded-pill items-center justify-center">
                  <img
                    src="https://imagedelivery.net/6SRmze-FT2n2PI_eCsJRmg/b0fd555d-c987-4959-56d8-dead0b49dc00/public"
                    className="hover:rotate-12 transition-all duration-300 ease-in-out p-3"
                    alt="logo"
                  />
                </div>
                <Mail className="h-6 w-6 text-muted-foreground absolute -bottom-1 -right-1 bg-background rounded-pill p-1" />
              </div>
            </div>
            <CardTitle className="text-2xl font-semibold text-foreground">
              Mail Access Required
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="bg-muted/50 rounded-lg p-4 border border-border">
              <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                Why we need this access
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Automatically outreach creators</li>
                <li>• Send follow-up emails and respond to creators</li>
              </ul>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-foreground mb-3">
                Connect a Mail Provider:
              </h3>

              {/* Google Provider */}
              <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-pill bg-background flex items-center justify-center">
                      <svg className="h-6 w-6" viewBox="0 0 24 24">
                        <path
                          fill="#EA4335"
                          d="M5.26620003,9.76452941 C6.19878754,6.93863203 8.85444915,4.90909091 12,4.90909091 C13.6909091,4.90909091 15.2181818,5.50909091 16.4181818,6.49090909 L19.9090909,3 C17.7818182,1.14545455 15.0545455,0 12,0 C7.27006974,0 3.1977497,2.69829785 1.23999023,6.65002441 L5.26620003,9.76452941 Z"
                        />
                        <path
                          fill="#34A853"
                          d="M16.0407269,18.0125889 C14.9509167,18.7163016 13.5660892,19.0909091 12,19.0909091 C8.86648613,19.0909091 6.21911939,17.076871 5.27698177,14.2678769 L1.23746264,17.3349879 C3.19279051,21.2936293 7.26500293,24 12,24 C14.9328362,24 17.7353462,22.9573905 19.834192,20.9995801 L16.0407269,18.0125889 Z"
                        />
                        <path
                          fill="#4A90E2"
                          d="M19.834192,20.9995801 C22.0291676,18.9520994 23.4545455,15.903663 23.4545455,12 C23.4545455,11.2909091 23.3454545,10.5272727 23.1818182,9.81818182 L12,9.81818182 L12,14.4545455 L18.4363636,14.4545455 C18.1187732,16.013626 17.2662994,17.2212117 16.0407269,18.0125889 L19.834192,20.9995801 Z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.27698177,14.2678769 C5.03832634,13.556323 4.90909091,12.7937589 4.90909091,12 C4.90909091,11.2182781 5.03443647,10.4668121 5.26620003,9.76452941 L1.23999023,6.65002441 C0.43658717,8.26043162 0,10.0753848 0,12 C0,13.9195484 0.444780743,15.7301709 1.23746264,17.3349879 L5.27698177,14.2678769 Z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        Google / Gmail
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {googleAccount ? "Connected" : "Not connected"}
                      </p>
                    </div>
                  </div>
                  {googlePermissionsGranted && (
                    <Badge variant="success">
                      <CheckCircle className="h-3 w-3" />
                      Ready
                    </Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Read Emails</span>
                    {hasGmailRead ? (
                      <CheckCircle className="h-4 w-4 text-success" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Send Emails</span>
                    {hasGmailSend ? (
                      <CheckCircle className="h-4 w-4 text-success" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleConnect("google")}
                    className="flex-1"
                    variant={googlePermissionsGranted ? "outline" : "default"}
                    size="sm"
                    disabled={googlePermissionsGranted}
                  >
                    {googlePermissionsGranted ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Connected
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        {googleAccount ? "Grant Permissions" : "Connect Google"}
                      </>
                    )}
                  </Button>
                  {googleAccount && (
                    <Button
                      onClick={() => handleDisconnect("google")}
                      variant="ghost"
                      size="sm"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Microsoft Provider */}
              <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-pill bg-background flex items-center justify-center">
                      <svg className="h-6 w-6" viewBox="0 0 23 23">
                        <path fill="#f3f3f3" d="M0 0h23v23H0z" />
                        <path fill="#f35325" d="M1 1h10v10H1z" />
                        <path fill="#81bc06" d="M12 1h10v10H12z" />
                        <path fill="#05a6f0" d="M1 12h10v10H1z" />
                        <path fill="#ffba08" d="M12 12h10v10H12z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        Microsoft / Outlook
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {microsoftAccount ? "Connected" : "Not connected"}
                      </p>
                    </div>
                  </div>
                  {microsoftPermissionsGranted && (
                    <Badge variant="success">
                      <CheckCircle className="h-3 w-3" />
                      Ready
                    </Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Read Emails</span>
                    {hasMicrosoftMailRead ? (
                      <CheckCircle className="h-4 w-4 text-success" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Send Emails</span>
                    {hasMicrosoftMailSend ? (
                      <CheckCircle className="h-4 w-4 text-success" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Manage Emails</span>
                    {hasMicrosoftMailReadWrite ? (
                      <CheckCircle className="h-4 w-4 text-success" />
                    ) : (
                      <XCircle className="h-4 w-4 text-destructive" />
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleConnect("microsoft")}
                    className="flex-1"
                    variant={
                      microsoftPermissionsGranted ? "outline" : "default"
                    }
                    size="sm"
                    disabled={microsoftPermissionsGranted}
                  >
                    {microsoftPermissionsGranted ? (
                      <>
                        <CheckCircle className="h-4 w-4" />
                        Connected
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4" />
                        {microsoftAccount
                          ? "Grant Permissions"
                          : "Connect Microsoft"}
                      </>
                    )}
                  </Button>
                  {microsoftAccount && (
                    <Button
                      onClick={() => handleDisconnect("microsoft")}
                      variant="ghost"
                      size="sm"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              We have been verified by Casa tier 2 by an external security team.
              We will never access your personal data or share it with third
              parties.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};
